import { ColumnDef } from '@tanstack/react-table'
import { ArrowUpDown, MoreHorizontal, Eye, Pencil, BarChart3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { Treatment, TreatmentType } from '@/lib/api-client'
import { formatCurrency, formatNumber } from '@/lib/utils'

// Category colors from treatmentuiplan.md
export const CATEGORY_COLORS: Record<string, string> = {
  'Pelvic & Intimate': '#ec4899',    // pink-500
  'Facial & Skin': '#8b5cf6',        // violet-500
  'Skin Boosters': '#06b6d4',        // cyan-500
  'Body Contouring': '#f59e0b',      // amber-500
  'Breast & Buttock': '#ef4444',     // red-500
  'RF Treatments': '#6366f1',        // indigo-500
  'Weight Loss': '#22c55e',          // green-500
  'Skin Procedures': '#64748b',      // slate-500
  'Consultation': '#0ea5e9',         // sky-500
  'Products': '#a855f7',             // purple-500
  'Legacy': '#9ca3af',               // gray-400
}

// Type badge colors
const TYPE_COLORS: Record<TreatmentType, string> = {
  treatment: '#3b82f6',   // blue-500
  product: '#10b981',     // emerald-500
  medication: '#f97316',  // orange-500
}

const TYPE_LABELS: Record<TreatmentType, string> = {
  treatment: 'Treatment',
  product: 'Product',
  medication: 'Medication',
}

interface TreatmentColumnsProps {
  onView: (treatment: Treatment) => void
  onEdit: (treatment: Treatment) => void
}

export function getTreatmentColumns({ onView, onEdit }: TreatmentColumnsProps): ColumnDef<Treatment>[] {
  return [
    // Selection checkbox
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          onClick={(e) => e.stopPropagation()}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },

    // Name
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="font-medium">{row.getValue('name')}</div>
      ),
    },

    // Category with colored badge
    {
      accessorKey: 'categoryName',
      header: 'Category',
      cell: ({ row }) => {
        const category = row.getValue('categoryName') as string | null
        const color = category ? CATEGORY_COLORS[category] || '#6b7280' : '#6b7280'
        return category ? (
          <Badge style={{ background: color }} className="text-white text-xs">
            {category}
          </Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      },
    },

    // Type with colored badge
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('type') as TreatmentType | null
        if (!type) return <span className="text-muted-foreground">-</span>
        const color = TYPE_COLORS[type]
        const label = TYPE_LABELS[type]
        return (
          <Badge style={{ background: color }} className="text-white text-xs">
            {label}
          </Badge>
        )
      },
    },

    // Sales count (invoice items sold in period)
    {
      accessorKey: 'itemCount',
      id: 'sales',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Sales
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const count = row.original.itemCount as number
        return <div className="text-center font-medium">{formatNumber(count)}</div>
      },
    },

    // Revenue
    {
      accessorKey: 'revenue',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-semibold px-0 hover:bg-transparent"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Revenue
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const revenue = row.getValue('revenue') as number
        return <div className="text-right font-medium">{formatCurrency(revenue)}</div>
      },
    },

    // Avg per Sale (calculated: revenue / sales)
    {
      id: 'avgPerSale',
      header: 'Avg/Sale',
      cell: ({ row }) => {
        const revenue = row.original.revenue as number
        const sales = row.original.itemCount as number
        const avg = sales > 0 ? revenue / sales : 0
        return <div className="text-right font-medium">{formatCurrency(avg)}</div>
      },
      enableSorting: false, // Calculated field, no server-side sorting
    },

    // Status badge
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => {
        const isActive = row.getValue('isActive') as boolean
        return (
          <Badge
            className="text-white text-xs"
            style={{ background: isActive ? '#22c55e' : '#ef4444' }}
          >
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
    },

    // Actions
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const treatment = row.original

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-9 w-9 p-0"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-[13px]">Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => onView(treatment)} className="text-[13px]">
                <Eye className="mr-2 h-4 w-4" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onEdit(treatment)} className="text-[13px]">
                <Pencil className="mr-2 h-4 w-4" />
                Edit Treatment
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onView(treatment)} className="text-[13px]">
                <BarChart3 className="mr-2 h-4 w-4" />
                View Analytics
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]
}
